import { Table, Button, TableColumn, TableHeaderCell, Chip, SearchBar } from 'common/components';
import { SquarePlay, Phone, PhoneOutgoing } from 'lucide-react';

import { useGetCallsData } from 'services/hooks/queries';
import { ManualCallbacks as ManualCallbacksType } from 'services/api/getCallsData';
import { useState } from 'react';
import { CallDataType, SortDirection, SortField } from 'types/call.type';
import { StatusSelect } from './components/StatusSelect';
import { useAgentCallConsole } from 'components/AgentCallConsole/hooks/useAgentCallConsole';
import { CallInfo } from 'types/agentCommandCenter.type';
import { differenceInHours, differenceInMinutes, formatDistance, subDays, subHours, subMinutes } from 'date-fns';
import { useNavigate } from 'react-router-dom';

import styles from './ManualCallbacks.module.scss';

const calculateCallbackAge = (record: CallDataType) => {
    const now = new Date();

    const isLessThan1Hour = differenceInMinutes(now, new Date(record.endTime)) < 60;
    const isOneToFourHours =
        differenceInHours(now, new Date(record.endTime)) < 4 && differenceInHours(now, new Date(record.endTime)) >= 1;
    const isFourToTwentyFourHours =
        differenceInHours(now, new Date(record.endTime)) < 24 && differenceInHours(now, new Date(record.endTime)) >= 4;
    const isMoreThanTwentyFourHours = differenceInHours(now, new Date(record.endTime)) > 24;

    return {
        isLessThan1Hour,
        isOneToFourHours,
        isFourToTwentyFourHours,
        isMoreThanTwentyFourHours,
    };
};

const CallbackAge = {
    ALL: 'ALL',
    LESS_THAN_1_HOUR: 'LESS_THAN_1_HOUR',
    ONE_TO_FOUR_HOURS: 'ONE_TO_FOUR_HOURS',
    FOUR_TO_TWENTY_FOUR_HOURS: 'FOUR_TO_TWENTY_FOUR_HOURS',
    MORE_THAN_24_HOURS: 'MORE_THAN_24_HOURS',
} as const;

const CallbackAgeLabels = {
    [CallbackAge.ALL]: 'All',
    [CallbackAge.LESS_THAN_1_HOUR]: 'Less than 1 hour',
    [CallbackAge.ONE_TO_FOUR_HOURS]: '1 to 4 hours',
    [CallbackAge.FOUR_TO_TWENTY_FOUR_HOURS]: '4 to 24 hours',
    [CallbackAge.MORE_THAN_24_HOURS]: 'More than 24 hours',
} as const;

type ManualCallbacksProps = {
    handleViewSyncTranscript: (callId: string, callInfo: CallInfo) => void;
};

export const ManualCallbacks = ({ handleViewSyncTranscript }: ManualCallbacksProps) => {
    const { handleMakeCall } = useAgentCallConsole();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [callbackAge, setCallbackAge] = useState<keyof typeof CallbackAge>(CallbackAge.ALL);
    const [dateRange, setDateRange] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({
        startDate: undefined,
        endDate: undefined,
    });
    const [sortField, setSortField] = useState<SortField>('callDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [searchTerm, setSearchTerm] = useState('');

    const {
        callsData,
        isCallsDataLoading,
        isCallsDataFetching,
        refetch: refetchCallsData,
    } = useGetCallsData(
        {
            manualCallback: ManualCallbacksType.NEEDED,
            currentPage: currentPage,
            perPage: rowsPerPage,
            startDate: dateRange?.startDate,
            endDate: dateRange?.endDate,
            sortBy: sortField,
            sortOrder: sortDirection,
            search: searchTerm,
        },
        true // isEnabled
    );

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    const callbackHandler = (callId: string) => {
        handleMakeCall(callId, {
            onSuccess: () => {
                refetchCallsData();
            },
        });
    };

    const handleCallbackAgeChange = (value: string) => {
        if (!value) return;
        setCallbackAge(value as keyof typeof CallbackAge);

        const now = new Date();
        switch (value) {
            case CallbackAge.LESS_THAN_1_HOUR:
                setDateRange({ startDate: subMinutes(now, 60), endDate: now });
                break;
            case CallbackAge.ONE_TO_FOUR_HOURS:
                setDateRange({ startDate: subHours(now, 4), endDate: subHours(now, 1) });
                break;
            case CallbackAge.FOUR_TO_TWENTY_FOUR_HOURS:
                setDateRange({ startDate: subHours(now, 24), endDate: subHours(now, 4) });
                break;
            case CallbackAge.MORE_THAN_24_HOURS:
                setDateRange({ startDate: subDays(now, 1), endDate: undefined });
                break;
            case CallbackAge.ALL:
                setDateRange({ startDate: undefined, endDate: undefined });
                break;
        }
    };

    const handleSortChange = (field: SortField, newSortDirection: SortDirection) => {
        setSortField(field);
        setSortDirection(newSortDirection);
    };
    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const handleViewContact = (phoneNumber: string) => {
        const encodedPhoneNumber = encodeURIComponent(phoneNumber);
        navigate(`/contact-manager?phoneNumber=${encodedPhoneNumber}`);
    };

    const columns: TableColumn<CallDataType>[] = [
        {
            key: 'callTime',
            title: (
                <TableHeaderCell
                    label='Wait'
                    sort={{
                        isSorted: sortField === 'callDate',
                        direction: sortDirection,
                        onSort: () => {
                            handleSortChange('callDate', sortDirection === 'asc' ? 'desc' : 'asc');
                        },
                    }}
                    filterOptions={{
                        options: [
                            ...Object.keys(CallbackAge).map((age) => ({
                                label: CallbackAgeLabels[age as keyof typeof CallbackAge],
                                value: age,
                            })),
                        ],
                        selectedValue: callbackAge || '',
                        onChange: handleCallbackAgeChange,
                    }}
                />
            ),
            render: (_, record) => {
                const { isLessThan1Hour, isOneToFourHours, isFourToTwentyFourHours } = calculateCallbackAge(record);
                return (
                    <div className='flex flex-col gap-xs'>
                        <div>
                            <Chip
                                color={
                                    isLessThan1Hour
                                        ? 'secondary'
                                        : isOneToFourHours
                                          ? 'secondary'
                                          : isFourToTwentyFourHours
                                            ? 'warning'
                                            : 'error'
                                }
                                corner='pill'
                                weight='semibold'
                            >
                                {formatDistance(new Date(), new Date(record.endTime))}
                            </Chip>
                        </div>
                    </div>
                );
            },
        },
        {
            key: 'contactName',
            title: 'Contact Name',
            dataIndex: 'contactName',
            render: (value, record) => {
                return (
                    <Button variant='link' onClick={() => handleViewContact(record.phoneNumber)}>
                        {value}
                    </Button>
                );
            },
        },
        {
            key: 'phoneNumber',
            title: 'Phone Number',
            dataIndex: 'phoneNumber',
        },
        {
            key: 'partyStage',
            title: 'Status',
            width: 160,
            render: (_, record) => (
                <StatusSelect callId={record.callId} contactId={record.contactId} currentStatus={record.partyStage} />
            ),
        },
        {
            key: 'transcript',
            title: 'Transcript',
            align: 'center',
            width: 100,
            render: (_, record) => (
                <Button
                    variant='outline-secondary'
                    size='sm'
                    iconOnly
                    isRounded
                    icon={SquarePlay}
                    onClick={() =>
                        handleViewSyncTranscript(record.callId, {
                            name: record.contactName,
                            callDate: new Date(record.startTime || record.scheduledDate).toLocaleDateString(),
                            callTime: new Date(record.startTime || record.scheduledDate).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                            }),
                        })
                    }
                />
            ),
        },
        {
            key: 'action',
            title: 'Action',
            align: 'center',
            render: (_, record) => (
                <Button variant='primary' size='sm' icon={Phone} onClick={() => callbackHandler(record.callId)}>
                    Callback
                </Button>
            ),
        },
    ];

    return (
        <div className={styles.manualCallbacksSection}>
            <div className='flex items-end gap-sm justify-between'>
                <h4 className='flex items-center gap-sm'>
                    <PhoneOutgoing color='var(--primary-purple)' size={16} />
                    Callback Requests
                </h4>
                <SearchBar
                    placeholder='Search by name, number or ID'
                    onSearch={setSearchTerm}
                    onClear={handleClearSearch}
                    debounceDelay={300}
                    className={styles.searchBar}
                />
            </div>
            <Table
                data={callsData?.data || []}
                columns={columns}
                emptyText='No call history available'
                rowKey='callId'
                size='medium'
                variant='secondary'
                isLoading={isCallsDataLoading || isCallsDataFetching}
                rowClassName={(record) => {
                    const { isMoreThanTwentyFourHours } = calculateCallbackAge(record);

                    return `${isMoreThanTwentyFourHours ? styles.tableRowError : ''}`;
                }}
                pagination={{
                    enabled: true,
                    onPageChange: handlePageChange,
                    onRowsPerPageChange: handleRowsPerPageChange,
                    currentPage,
                    totalPages: callsData?.pagination.totalPages || 1,
                    totalRecords: callsData?.pagination.totalRecords || 0,
                    rowsPerPage,
                }}
            />
        </div>
    );
};
